import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, TextCTA } from "@/components/CTA";
import { Tick, Star } from "@/components/Annotation";
import { Tape, StickyNote, PunchHoles, SectionLabel, BoxedNote } from "@/components/Notebook";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES, BRAND } from "@/config/links";
import { useLang } from "@/i18n";

export default function About() {
  const { t } = useLang();
  const firstName = BRAND.coach.split(" ")[0];

  const principles = t("about.principles");
  const layers = t("about.holistic_layers");
  const values = t("about.values");
  const education = t("about.credentials_education");
  const background = t("about.credentials_background");
  const expertise = t("about.credentials_expertise");

  const credentialCols = [
    { title: t("about.credentials_col_education"), items: education },
    { title: t("about.credentials_col_background"), items: background },
    { title: t("about.credentials_col_expertise"), items: expertise },
  ];

  return (
    <>
      <PageMeta
        titleKey="meta.about_title"
        descriptionKey="meta.about_description"
        path="/about"
      />
      <PageHero
        testId="about-hero"
        eyebrow={t("about.hero_eyebrow")}
        headline={[t("about.hero_h1_a"), t("about.hero_h1_b")]}
        emphasis={t("about.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("about.hero_intro")}
      />

      {/* portrait + intro */}
      <section className="relative z-10 pb-24 md:pb-32" data-testid="about-portrait">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <Tape className="absolute -top-4 left-8" rotate={-6} width={120} />
              <Tape className="absolute -top-4 right-8" rotate={5} width={120} />
              <div className="aspect-[4/5] bg-paper-deep/40 border border-ink/10 overflow-hidden">
                <img
                  src="/sketches/coach-portrait.webp"
                  srcSet="/sketches/coach-portrait-500.webp 500w, /sketches/coach-portrait.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 90vw"
                  alt={t("about.portrait_alt", { name: BRAND.coach })}
                  className="w-full h-full object-cover object-[center_top]"
                  style={{ mixBlendMode: "multiply", filter: "contrast(1.05)" }}
                  draggable={false}
                  data-testid="about-coach-portrait"
                />
              </div>
              <p className="mt-3 font-hand text-terracotta text-xl">
                {t("about.portrait_caption", { name: BRAND.coach })}
              </p>
            </div>
          </Reveal>

          <Reveal
            delay={0.15}
            className="lg:col-span-7 space-y-6 font-body text-lg text-ink-soft leading-relaxed"
          >
            <SectionLabel className="mb-4 block">{t("about.bio_label")}</SectionLabel>
            <p>{t("about.bio_p1", { name: BRAND.coach, company: BRAND.company })}</p>
            <p>
              {t("about.bio_p2_a")}
              <span className="ink-underline">{t("about.bio_p2_underlined")}</span>
              {t("about.bio_p2_c")}
            </p>
            <p className="font-serif italic text-2xl text-ink">{t("about.bio_p3")}</p>
            <p>{t("about.bio_p4")}</p>
          </Reveal>
        </div>
      </section>

      {/* belief pull-quote */}
      <section className="relative z-10 py-24 md:py-36 bg-paper-deep/50 border-y border-ink/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">
              {t("about.belief_eyebrow")}
            </p>
            <blockquote className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight text-ink">
              {t("about.belief_h1")}
              <br />
              {t("about.belief_h2")}
              <br />
              <span className="italic text-terracotta">{t("about.belief_h3")}</span>
            </blockquote>
            <p className="mt-8 font-body text-lg text-ink-soft max-w-3xl mx-auto leading-relaxed">
              {t("about.belief_body")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* rehearsal room philosophy */}
      <section className="relative z-10 py-24 md:py-36" data-testid="about-philosophy">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("about.philosophy_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("about.philosophy_h_a")}
              <br />
              <span className="italic">{t("about.philosophy_h_b")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
              {t("about.philosophy_p1")}
            </p>
            <p className="mt-4 font-body text-lg text-ink-soft leading-relaxed">
              {t("about.philosophy_p2")}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.10)] p-8 md:p-10 md:pl-16">
              <PunchHoles className="hidden md:flex absolute left-5 top-8 bottom-8" count={9} />
              <SectionLabel className="mb-6 block">{t("about.principles_label")}</SectionLabel>
              <ul className="space-y-3">
                {Array.isArray(principles) &&
                  principles.map((p, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="font-hand text-terracotta text-lg leading-none mt-1 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-serif text-lg md:text-xl text-ink leading-snug">
                        {p}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* holistic stage presence */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40"
        data-testid="about-holistic"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("about.holistic_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("about.holistic_h_a")}
              <br />
              {t("about.holistic_h_b_a")}
              <span className="italic">{t("about.holistic_h_b_emph")}</span>
              {t("about.holistic_h_b_c")}
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[52ch]">
              {t("about.holistic_body")}
            </p>
            <div className="mt-8">
              <TextCTA href="/how-we-work">{t("about.holistic_cta")}</TextCTA>
            </div>
          </Reveal>

          <StaggerGroup className="lg:col-span-6 grid grid-cols-2 gap-3">
            {Array.isArray(layers) &&
              layers.map((h) => (
                <StaggerItem key={h}>
                  <div className="bg-paper border border-ink/12 px-4 py-3 text-center">
                    <span className="font-hand text-ink text-lg">{h}</span>
                  </div>
                </StaggerItem>
              ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Greece → Brazil */}
      <section className="relative z-10 py-24 md:py-32" data-testid="about-brazil">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">
              {t("about.brazil_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("about.brazil_h_a")}
              <br />
              <span className="italic">{t("about.brazil_h_b")}</span>
            </h2>
            <p className="mt-8 font-body text-lg text-ink-soft leading-relaxed max-w-3xl mx-auto">
              {t("about.brazil_body")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* credentials */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="about-credentials"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("about.credentials_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("about.credentials_h")}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {credentialCols.map((col) => (
              <Reveal key={col.title}>
                <SectionLabel className="mb-5 block">{col.title}</SectionLabel>
                <ul className="space-y-2">
                  {Array.isArray(col.items) &&
                    col.items.map((it) => (
                      <li key={it} className="flex items-start gap-3">
                        <Tick className="w-4 h-4 mt-1 shrink-0" />
                        <span className="font-body text-ink leading-relaxed">{it}</span>
                      </li>
                    ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* values */}
      <section className="relative z-10 py-24 md:py-32" data-testid="about-values">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("about.values_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("about.values_h_a")}
              <br />
              <span className="italic">{t("about.values_h_b")}</span>
            </h2>
          </Reveal>
          <div className="lg:col-span-7 flex flex-wrap gap-3">
            {Array.isArray(values) &&
              values.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-ink/25 text-ink font-body text-sm"
                >
                  <Star className="w-3.5 h-3.5" />
                  {v}
                </span>
              ))}
          </div>
        </div>
      </section>

      {/* the company */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40"
        data-testid="about-company"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("about.company_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink mb-8">
              {t("about.company_h_a")}
              <br />
              <span className="italic">{t("about.company_h_b")}</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <div className="font-body text-lg text-ink-soft leading-relaxed space-y-5">
                <p>{t("about.company_p1", { company: BRAND.company })}</p>
                <p>{t("about.company_p2")}</p>
                <p>{t("about.company_p3")}</p>
              </div>
              <div className="font-body text-lg text-ink-soft leading-relaxed space-y-5">
                <p>{t("about.company_p4", { company: BRAND.company })}</p>
                <p>{t("about.company_p5")}</p>
                <BoxedNote tone="text-terracotta">
                  <p className="font-serif italic text-2xl text-ink leading-snug">
                    {t("about.company_note")}
                  </p>
                </BoxedNote>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* mission + CTA */}
      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="about-mission">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">
              {t("about.mission_eyebrow")}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-paper max-w-4xl mx-auto">
              {t("about.mission_h")}
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center">
            <StickyNote rotate={-3} color="#F2EBDA">
              {t("about.mission_note")}
            </StickyNote>
          </Reveal>
          <Reveal delay={0.35} className="mt-12">
            <PrimaryCTA href={ROUTES.booking} testId="about-book-cta" location="final_cta" targetService="general_booking">
              {t("cta.book_consultation")}
            </PrimaryCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
